import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext, apiClient } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft, Phone, Mail, Shield, ShieldCheck, ShieldAlert,
  Clock, AlertTriangle, CheckCircle, XCircle, Send, Building2
} from 'lucide-react';

const AdminUserControls = () => {
  const { userId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState([]);
  const [reason, setReason] = useState('');

  // Editable fields
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [gameActivitySms, setGameActivitySms] = useState(true);
  const [remindersSms, setRemindersSms] = useState(true);
  const [importantSms, setImportantSms] = useState(true);
  const [allowlistBypass, setAllowlistBypass] = useState(false);

  useEffect(() => {
    if (!user || !['admin', 'scout'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    fetchData();
    fetchAudit();
  }, [userId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/admin/users/${userId}/contact-controls`);
      const d = res.data;
      setData(d);
      setEmail(d.contact.email || '');
      setPhone(d.contact.phone_number || '');
      setSmsOptIn(d.sms_preferences.sms_opt_in);
      setGameActivitySms(d.sms_preferences.game_activity_sms);
      setRemindersSms(d.sms_preferences.reminders_sms);
      setImportantSms(d.sms_preferences.important_sms);
      setAllowlistBypass(d.routing.allowlist_bypass);
    } catch (err) {
      toast.error('Failed to load user controls');
    } finally {
      setLoading(false);
    }
  };

  const fetchAudit = async () => {
    try {
      const res = await apiClient.get(`/admin/users/${userId}/contact-audit`);
      setAudit(res.data.entries || []);
    } catch {
      // silent
    }
  };

  const handleSave = async () => {
    if (!reason.trim()) {
      toast.error('A reason is required for audit logging');
      return;
    }
    setSaving(true);
    try {
      const payload = { reason: reason.trim() };
      if (email !== data.contact.email) payload.email = email;
      if (phone !== (data.contact.phone_number || '')) payload.phone_number = phone || null;
      if (smsOptIn !== data.sms_preferences.sms_opt_in) payload.sms_opt_in = smsOptIn;
      if (gameActivitySms !== data.sms_preferences.game_activity_sms) payload.game_activity_sms = gameActivitySms;
      if (remindersSms !== data.sms_preferences.reminders_sms) payload.reminders_sms = remindersSms;
      if (importantSms !== data.sms_preferences.important_sms) payload.important_sms = importantSms;
      if (allowlistBypass !== data.routing.allowlist_bypass) payload.allowlist_bypass = allowlistBypass;

      const res = await apiClient.put(`/admin/users/${userId}/contact-controls`, payload);
      toast.success(res.data.message);
      setReason('');
      fetchData();
      fetchAudit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const deliveryBadge = (mode) => {
    if (mode === 'production') return <Badge className="bg-green-100 text-green-800 border-green-200">Production</Badge>;
    if (mode === 'allowlist_bypass') return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Allowlist Bypass</Badge>;
    return <Badge className="bg-zinc-100 text-zinc-600 border-zinc-200">Safe Routed</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <p className="text-zinc-500">Loading...</p>
      </div>
    );
  }

  if (!data) return null;

  const hasChanges = email !== (data.contact.email || '') ||
    phone !== (data.contact.phone_number || '') ||
    smsOptIn !== data.sms_preferences.sms_opt_in ||
    gameActivitySms !== data.sms_preferences.game_activity_sms ||
    remindersSms !== data.sms_preferences.reminders_sms ||
    importantSms !== data.sms_preferences.important_sms ||
    allowlistBypass !== data.routing.allowlist_bypass;

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <Button variant="ghost" onClick={() => navigate('/admin')} className="mb-4 px-0 text-zinc-500 hover:text-zinc-900" data-testid="back-to-admin">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <h1 className="text-xl font-bold text-zinc-900" data-testid="admin-user-title">
            {data.username}
          </h1>
          {deliveryBadge(data.routing.delivery_mode)}
          {user?.role === 'admin' && (
            <Button
              variant="outline"
              size="sm"
              className="ml-auto text-purple-600"
              onClick={() => navigate(`/admin/hosts/${userId}`)}
              data-testid="view-host-data-btn"
            >
              <Building2 className="w-4 h-4 mr-2" /> View Host Data
            </Button>
          )}
        </div>

        {/* Contact Info */}
        <Card className="mb-4" data-testid="contact-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-zinc-500 flex items-center gap-2">
              <Mail className="w-4 h-4" /> Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs text-zinc-500 mb-1 block">Email</Label>
              <Input value={email} onChange={e => setEmail(e.target.value)} data-testid="admin-email-input" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1 block">Phone Number (E.164)</Label>
              <div className="flex items-center gap-2">
                <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1XXXXXXXXXX" data-testid="admin-phone-input" />
                {data.contact.phone_missing && (
                  <Badge variant="outline" className="text-amber-600 border-amber-300 whitespace-nowrap flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Missing
                  </Badge>
                )}
                {!data.contact.phone_missing && !data.contact.phone_valid && (
                  <Badge variant="outline" className="text-red-600 border-red-300 whitespace-nowrap flex items-center gap-1">
                    <XCircle className="w-3 h-3" /> Invalid
                  </Badge>
                )}
                {data.contact.phone_valid && (
                  <Badge variant="outline" className="text-green-600 border-green-300 whitespace-nowrap flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Valid
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SMS Preferences */}
        <Card className="mb-4" data-testid="sms-prefs-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-zinc-500 flex items-center gap-2">
              <Phone className="w-4 h-4" /> SMS Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-900">SMS Master Opt-In</p>
                <p className="text-xs text-zinc-500">User-set preference</p>
              </div>
              <Switch checked={smsOptIn} onCheckedChange={setSmsOptIn} data-testid="admin-sms-optin-toggle" />
            </div>
            <div className="border-t border-zinc-100 pt-3 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-zinc-700">Game Activity</p>
                <Switch checked={gameActivitySms} onCheckedChange={setGameActivitySms} data-testid="admin-game-activity-toggle" />
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-zinc-700">Reminders</p>
                <Switch checked={remindersSms} onCheckedChange={setRemindersSms} data-testid="admin-reminders-toggle" />
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-zinc-700">Important Updates</p>
                <Switch checked={importantSms} onCheckedChange={setImportantSms} data-testid="admin-important-toggle" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Routing Controls */}
        <Card className="mb-4" data-testid="routing-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-zinc-500 flex items-center gap-2">
              <Shield className="w-4 h-4" /> Delivery Routing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-900">Safe Mode</p>
                <p className="text-xs text-zinc-500">Global setting (env-controlled)</p>
              </div>
              <Badge className={data.routing.safe_mode_active ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                {data.routing.safe_mode_active ? 'Active' : 'Off'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-zinc-900">Allowlist Bypass</p>
                <p className="text-xs text-zinc-500">Send real notifications to this user</p>
              </div>
              <Switch checked={allowlistBypass} onCheckedChange={setAllowlistBypass} data-testid="admin-allowlist-toggle" />
            </div>
            <div className="p-3 bg-zinc-50 rounded-lg text-xs text-zinc-600 space-y-1">
              <p><span className="font-medium">Delivery mode:</span> {data.routing.delivery_mode}</p>
              {data.routing.safe_mode_active && !allowlistBypass && (
                <p><span className="font-medium">Safe SMS:</span> {data.routing.safe_phone}</p>
              )}
              {data.routing.safe_mode_active && !allowlistBypass && (
                <p><span className="font-medium">Safe Email:</span> {data.routing.safe_email}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Last Activity */}
        <Card className="mb-4" data-testid="activity-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-zinc-500 flex items-center gap-2">
              <Clock className="w-4 h-4" /> Last Notification Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-500">Last SMS</span>
              <span className="text-zinc-900 font-medium">
                {data.last_sms.timestamp ? new Date(data.last_sms.timestamp).toLocaleString() : 'Never'}
              </span>
            </div>
            {data.last_sms.routing_action && (
              <div className="flex justify-between">
                <span className="text-zinc-500">SMS Routing</span>
                <span className="text-zinc-700">{data.last_sms.routing_action}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-zinc-500">Last Email</span>
              <span className="text-zinc-900 font-medium">
                {data.last_email.timestamp ? new Date(data.last_email.timestamp).toLocaleString() : 'Never'}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Save Section */}
        {hasChanges && (
          <Card className="mb-4 border-orange-200 bg-orange-50" data-testid="save-card">
            <CardContent className="pt-4 space-y-3">
              <p className="text-sm font-medium text-orange-800">Unsaved changes — provide a reason:</p>
              <Input
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Reason for change (required for audit)"
                data-testid="admin-reason-input"
              />
              <Button
                onClick={handleSave}
                disabled={saving || !reason.trim()}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="admin-save-btn"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Audit Log */}
        {audit.length > 0 && (
          <Card data-testid="audit-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
                Audit Log
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {audit.map(entry => (
                <div key={entry.id} className="p-3 bg-zinc-50 rounded-lg text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="font-medium text-zinc-700">{entry.admin_username}</span>
                    <span className="text-zinc-400">{new Date(entry.timestamp).toLocaleString()}</span>
                  </div>
                  <p className="text-zinc-600 italic">"{entry.reason}"</p>
                  {entry.changes.map((c) => (
                    <p key={`${entry.id}-${c.field}`} className="text-zinc-500">
                      <span className="font-medium">{c.field}:</span>{' '}
                      <span className="text-red-500 line-through">{String(c.old ?? 'null')}</span>{' → '}
                      <span className="text-green-600">{String(c.new ?? 'null')}</span>
                    </p>
                  ))}
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminUserControls;
